import React, { useState, useEffect } from 'react';
import { IdsButton, IdsSectionNotice } from '@investec/ids-web-react';
import { useUnifiedAnalytics } from '../../contexts/AnalyticsWrapper';

import './isaTransferProviderAmount.scss';

interface IsaTransferProviderAmountStepProps {
    sourceBalance: number;
    initialData?: ProviderAmountData | null;
    onBack: () => void;
    onContinue: (amountDetails: ProviderAmountData) => void;
}

export interface ProviderAmountData {
    transferType: 'full' | 'partial';
    totalTransferAmount: number | null;
    includesCurrentTaxYear: boolean;
    currentTaxYearAmount: number | null;
}

const MIN_AMOUNT = 0.01;
const MAX_AMOUNT = 1000000.00;
const MAX_TAX_YEAR_AMOUNT = 20000.00;

const formatWithCommas = (value: string): string => {
    const clean = value.replace(/[^0-9.]/g, '');
    const parts = clean.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 2)}` : parts[0];
};

const validatePartialAmount = (value: string): string | null => {
    if (!value || value.trim() === '') {
        return 'Please enter the amount you want to transfer';
    }

    const cleanValue = value.replace(/[£,\s]/g, '').trim();

    if (!/^\d+(\.\d{0,2})?$/.test(cleanValue)) {
        return 'Please enter a valid amount using numbers only';
    }

    const numericValue = parseFloat(cleanValue);

    if (isNaN(numericValue)) {
        return 'Please enter a valid amount';
    }

    if (numericValue < MIN_AMOUNT) {
        return 'Amount must be at least £0.01';
    }

    if (numericValue >= MAX_AMOUNT) {
        return 'Amount must be less than £1,000,000';
    }

    return null;
};

const validateTaxYearAmount = (value: string, totalAmount: string): string | null => {
    if (!value || value.trim() === '') {
        return "Please enter the amount you've contributed this tax year";
    }

    const cleanValue = value.replace(/[£,\s]/g, '').trim();

    if (!/^\d+(\.\d{0,2})?$/.test(cleanValue)) {
        return 'Please enter a valid amount using numbers only';
    }

    const numericValue = parseFloat(cleanValue);

    if (isNaN(numericValue)) {
        return 'Please enter a valid amount';
    }

    if (numericValue < MIN_AMOUNT) {
        return 'Enter an amount more than £0';
    }

    if (numericValue > MAX_TAX_YEAR_AMOUNT) {
        return 'Enter an amount of £20,000 or less';
    }

    if (totalAmount) {
        const totalClean = totalAmount.replace(/[£,\s]/g, '').trim();
        const totalNumeric = parseFloat(totalClean);
        if (!isNaN(totalNumeric) && numericValue > totalNumeric) {
            return 'The amount you have paid in this tax year cannot be more than your total transfer amount';
        }
    }

    return null;
};

const IsaTransferProviderAmountStep: React.FC<IsaTransferProviderAmountStepProps> = ({
    sourceBalance,
    initialData,
    onBack,
    onContinue
}) => {
    const analytics = useUnifiedAnalytics();
    const [transferType, setTransferType] = useState<'full' | 'partial' | null>(initialData?.transferType ?? null);
    const [includesCurrentTaxYear, setIncludesCurrentTaxYear] = useState<boolean | null>(initialData?.includesCurrentTaxYear ?? null);

    useEffect(() => {
        if (analytics) {
            analytics.trackAnalyticsPageEvent(
                'provider amount view',
                {},
                'ISA Transfer Provider Amount',
                'ISA_Transfer_Provider_Amount|Page_Load|Amount_View'
            );
        }
    }, [analytics]);

    const [totalTransferAmount, setTotalTransferAmount] = useState(
        initialData?.transferType === 'partial' && initialData?.totalTransferAmount != null
            ? initialData.totalTransferAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : ''
    );
    const [totalAmountTouched, setTotalAmountTouched] = useState(false);
    const [totalAmountError, setTotalAmountError] = useState<string | null>(null);
    const [currentTaxYearAmount, setCurrentTaxYearAmount] = useState(
        initialData?.currentTaxYearAmount != null
            ? initialData.currentTaxYearAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : ''
    );
    const [currentTaxYearAmountTouched, setCurrentTaxYearAmountTouched] = useState(false);
    const [currentTaxYearAmountError, setCurrentTaxYearAmountError] = useState<string | null>(null);

    const isFormValid = (): boolean => {
        if (!transferType) return false;
        if (includesCurrentTaxYear === null) return false;
        if (transferType === 'partial') {
            const amountError = validatePartialAmount(totalTransferAmount);
            if (amountError) return false;

            if (includesCurrentTaxYear) {
                const taxYearError = validateTaxYearAmount(currentTaxYearAmount, totalTransferAmount);
                if (taxYearError) return false;
            }
        }
        return true;
    };

    const handleContinue = () => {
        if (!isFormValid() || !transferType || includesCurrentTaxYear === null) return;

        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'continue',
                '',
                'isa transfer provider amount',
                'ISA Transfer',
                '',
                {},
                'click',
                'ISA_Transfer_Provider_Amount|Button_Click|Continue',
                'ISA Transfer Provider Amount'
            );
        }

        const parseValue = (val: string): number | null => {
            const cleaned = val.replace(/[£,\s]/g, '').trim();
            const num = parseFloat(cleaned);
            return isNaN(num) ? null : num;
        };

        onContinue({
            transferType,
            totalTransferAmount: parseValue(totalTransferAmount),
            includesCurrentTaxYear,
            currentTaxYearAmount: parseValue(currentTaxYearAmount)
        });
    };

    const handleCurrentTaxYearSelect = (value: boolean) => {
        setIncludesCurrentTaxYear(value);
        if (!value) {
            setCurrentTaxYearAmount('');
            setCurrentTaxYearAmountTouched(false);
            setCurrentTaxYearAmountError(null);
        }
    };

    const handleTransferTypeSelect = (type: 'full' | 'partial') => {
        setTransferType(type);
        setIncludesCurrentTaxYear(null);
        setTotalTransferAmount('');
        setTotalAmountTouched(false);
        setTotalAmountError(null);
        setCurrentTaxYearAmount('');
        setCurrentTaxYearAmountTouched(false);
        setCurrentTaxYearAmountError(null);
    };

    return (
        <main className="isa-transfer-provider-amount">
            <p className="isa-transfer-provider-amount__title">How much of your cash ISA would you like to transfer to us?</p>

            <p className="isa-transfer-provider-amount__info">
                You must transfer all contributions made in the current tax year.
                You can transfer all or part of your contributions from previous tax years,
                if your current provider allows it.
            </p>

            <div className="isa-transfer-provider-amount__cards">
                <button
                    type="button"
                    className={`isa-transfer-provider-amount__card${transferType === 'full' ? ' isa-transfer-provider-amount__card--selected' : ''}`}
                    onClick={() => handleTransferTypeSelect('full')}
                >
                    <span className="isa-transfer-provider-amount__card-radio">
                        {transferType === 'full' && <span className="isa-transfer-provider-amount__card-radio-dot" />}
                    </span>
                    <span className="isa-transfer-provider-amount__card-label">Full amount</span>
                </button>

                <button
                    type="button"
                    className={`isa-transfer-provider-amount__card${transferType === 'partial' ? ' isa-transfer-provider-amount__card--selected' : ''}`}
                    onClick={() => handleTransferTypeSelect('partial')}
                >
                    <span className="isa-transfer-provider-amount__card-radio">
                        {transferType === 'partial' && <span className="isa-transfer-provider-amount__card-radio-dot" />}
                    </span>
                    <span className="isa-transfer-provider-amount__card-label">Partial amount</span>
                </button>
            </div>

            {transferType === 'full' && (
                <div className="isa-transfer-provider-amount__tax-year">
                    <p className="isa-transfer-provider-amount__tax-year-question">
                        Does this include money contributed in the current tax year?
                    </p>
                    <div className="isa-transfer-provider-amount__tax-year-radios">
                        <button
                            type="button"
                            className={`isa-transfer-provider-amount__tax-year-radio${includesCurrentTaxYear === true ? ' isa-transfer-provider-amount__tax-year-radio--selected' : ''}`}
                            onClick={() => handleCurrentTaxYearSelect(true)}
                        >
                            <span className="isa-transfer-provider-amount__tax-year-radio-circle">
                                {includesCurrentTaxYear === true && <span className="isa-transfer-provider-amount__tax-year-radio-dot" />}
                            </span>
                            <span>Yes</span>
                        </button>
                        <button
                            type="button"
                            className={`isa-transfer-provider-amount__tax-year-radio${includesCurrentTaxYear === false ? ' isa-transfer-provider-amount__tax-year-radio--selected' : ''}`}
                            onClick={() => handleCurrentTaxYearSelect(false)}
                        >
                            <span className="isa-transfer-provider-amount__tax-year-radio-circle">
                                {includesCurrentTaxYear === false && <span className="isa-transfer-provider-amount__tax-year-radio-dot" />}
                            </span>
                            <span>No</span>
                        </button>
                    </div>
                </div>
            )}

            {transferType === 'full' && includesCurrentTaxYear === true && (
                <IdsSectionNotice
                    title="You must transfer all ISA contributions from the current tax year"
                    className="isa-transfer-provider-amount__notice"
                >
                    This means your transfer must match or exceed what you've contributed in the current tax year, plus any amount you like from previous tax years.
                </IdsSectionNotice>
            )}

            {transferType === 'partial' && (
                <div className="isa-transfer-provider-amount__partial">
                    <p className="isa-transfer-provider-amount__partial-heading">
                        Please specify the amount
                    </p>

                    <div className="isa-transfer-provider-amount__partial-field">
                        <label className="isa-transfer-provider-amount__partial-label">Total transfer amount</label>
                        <div className={`isa-transfer-provider-amount__amount-input-wrapper${totalAmountError && totalAmountTouched ? ' isa-transfer-provider-amount__amount-input-wrapper--error' : ''}`}>
                            <span className="isa-transfer-provider-amount__currency-symbol">£</span>
                            <input
                                type="text"
                                className="isa-transfer-provider-amount__amount-input"
                                value={totalTransferAmount}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/[^0-9.]/g, '');
                                    const formatted = formatWithCommas(raw);
                                    const shouldValidate = totalAmountTouched || formatted !== '';
                                    const error = shouldValidate ? validatePartialAmount(formatted) : null;
                                    setTotalTransferAmount(formatted);
                                    setTotalAmountTouched(shouldValidate);
                                    setTotalAmountError(error);
                                    // Revalidate tax year amount if it exists
                                    if (includesCurrentTaxYear && currentTaxYearAmount) {
                                        const taxError = validateTaxYearAmount(currentTaxYearAmount, formatted);
                                        setCurrentTaxYearAmountError(taxError);
                                    }
                                }}
                                onBlur={() => {
                                    setTotalAmountTouched(true);
                                    setTotalAmountError(validatePartialAmount(totalTransferAmount));
                                }}
                                placeholder=""
                                aria-invalid={!!(totalAmountError && totalAmountTouched)}
                                aria-describedby={totalAmountError && totalAmountTouched ? 'partial-amount-error' : undefined}
                            />
                        </div>
                        {totalAmountError && totalAmountTouched && (
                            <div className="isa-transfer-provider-amount__amount-error" id="partial-amount-error" role="alert">
                                {totalAmountError}
                            </div>
                        )}
                    </div>

                    <div className="isa-transfer-provider-amount__partial-tax-year">
                        <p className="isa-transfer-provider-amount__partial-tax-year-question">
                            Does the total amount you're transferring to us include any ISA contributions you've already made this tax year?
                        </p>
                        <div className="isa-transfer-provider-amount__partial-tax-year-radios">
                            <button
                                type="button"
                                className={`isa-transfer-provider-amount__partial-tax-year-radio${includesCurrentTaxYear === true ? ' isa-transfer-provider-amount__partial-tax-year-radio--selected' : ''}`}
                                onClick={() => handleCurrentTaxYearSelect(true)}
                            >
                                <span className="isa-transfer-provider-amount__partial-tax-year-radio-circle">
                                    {includesCurrentTaxYear === true && <span className="isa-transfer-provider-amount__partial-tax-year-radio-dot" />}
                                </span>
                                <span>Yes</span>
                            </button>
                            <button
                                type="button"
                                className={`isa-transfer-provider-amount__partial-tax-year-radio${includesCurrentTaxYear === false ? ' isa-transfer-provider-amount__partial-tax-year-radio--selected' : ''}`}
                                onClick={() => handleCurrentTaxYearSelect(false)}
                            >
                                <span className="isa-transfer-provider-amount__partial-tax-year-radio-circle">
                                    {includesCurrentTaxYear === false && <span className="isa-transfer-provider-amount__partial-tax-year-radio-dot" />}
                                </span>
                                <span>No</span>
                            </button>
                        </div>
                    </div>

                    {includesCurrentTaxYear === true && (
                        <IdsSectionNotice
                            title="You must transfer all ISA contributions from the current tax year"
                            className="isa-transfer-provider-amount__notice"
                        >
                            This means your transfer must match or exceed what you've contributed in the current tax year, plus any amount you like from previous tax years.
                        </IdsSectionNotice>
                    )}

                    {includesCurrentTaxYear === true && (
                        <div className="isa-transfer-provider-amount__partial-tax-year-amount">
                            <p className="isa-transfer-provider-amount__partial-tax-year-amount-text">
                                Enter the amount you've paid into your ISA during this tax year.
                            </p>
                            <p className="isa-transfer-provider-amount__partial-tax-year-amount-sub">
                                This should be the total for the current tax year
                            </p>
                            <div className={`isa-transfer-provider-amount__amount-input-wrapper${currentTaxYearAmountError && currentTaxYearAmountTouched ? ' isa-transfer-provider-amount__amount-input-wrapper--error' : ''}`}>
                                <span className="isa-transfer-provider-amount__currency-symbol">£</span>
                                <input
                                    type="text"
                                    className="isa-transfer-provider-amount__amount-input"
                                    value={currentTaxYearAmount}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/[^0-9.]/g, '');
                                        const formatted = formatWithCommas(raw);
                                        const shouldValidate = currentTaxYearAmountTouched || formatted !== '';
                                        const error = shouldValidate ? validateTaxYearAmount(formatted, totalTransferAmount) : null;
                                        setCurrentTaxYearAmount(formatted);
                                        setCurrentTaxYearAmountTouched(shouldValidate);
                                        setCurrentTaxYearAmountError(error);
                                    }}
                                    onBlur={() => {
                                        setCurrentTaxYearAmountTouched(true);
                                        setCurrentTaxYearAmountError(validateTaxYearAmount(currentTaxYearAmount, totalTransferAmount));
                                    }}
                                    placeholder=""
                                    aria-invalid={!!(currentTaxYearAmountError && currentTaxYearAmountTouched)}
                                    aria-describedby={currentTaxYearAmountError && currentTaxYearAmountTouched ? 'current-tax-year-amount-error' : undefined}
                                />
                            </div>
                            {currentTaxYearAmountError && currentTaxYearAmountTouched && (
                                <div className="isa-transfer-provider-amount__amount-error" id="current-tax-year-amount-error" role="alert">
                                    {currentTaxYearAmountError}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="isa-transfer-provider-amount__actions">
                <IdsButton appearance="default" size="md" onClick={() => {
                    if (analytics) {
                        analytics.trackAnalyticsClickEvent(
                            'back',
                            '',
                            'isa transfer provider amount',
                            'ISA Transfer',
                            '',
                            {},
                            'click',
                            'ISA_Transfer_Provider_Amount|Button_Click|Back',
                            'ISA Transfer Provider Amount'
                        );
                    }
                    onBack();
                }}>
                    Back
                </IdsButton>
                <IdsButton
                    appearance="primary"
                    size="md"
                    disabled={!isFormValid()}
                    onClick={handleContinue}
                >
                    Continue
                </IdsButton>
            </div>
        </main>
    );
};

export default IsaTransferProviderAmountStep;